/**
 * Handles the plp lazy loading and back button functionality
 * Lazy loads the images once window loads.
 * Fixes the correct page position if going back to plp.
 * @module plpLazyLoad
 */

import $ from 'jquery';
import registerJQueryPlugin from 'styleguide/lib/register-jquery-plugin';
import mergeOptions from 'styleguide/lib/merge-options'
import throttle from 'lodash/throttle';
import { checkAndHideZeroReviewElements } from './utils';

export const PLUGIN_NAME = 'plpLazyLoad';
export const DEFAULTS = {
  sessionName: '',
  loadAfterCallback: function() {}
};

/**
 * This will check if current Session exists
 * If not, create a new Session Storage and sets the new data into the Session Storage
 * If Session storage already has a value, delete that current value
 * and then set it with the new result value
 */
const setSessionData = (result, lazyLoadUrl, sessionName) => {
  let plpSessionData = sessionStorage.getItem(sessionName);
  plpSessionData = JSON.parse(plpSessionData) || [];

  const plpSessionDataJSON = {
    currentUrl: window.location.href,
    plpData: result,
    lazyLoadUrl,
  }

  // only allow 1 session data
  // overwrites the data if there's more than 1 data set
  if(plpSessionData.length > 0) {
    plpSessionData.shift();
  }
  plpSessionData.push(plpSessionDataJSON)
  try {
    sessionStorage.setItem(sessionName, JSON.stringify(plpSessionData));
  } catch(e) {
    console.warn("session storage error:", e);
  }
};

/**
 * Misc. cleanup after appending PLP results
 * @param  {jQuery} $el - $el
 */
const plpAppendCleanup = ($el, options = {}, result, lazyAnimation) => {
  $el.parent().css('min-height', '');
  $el.append(result);
  options.loadAfterCallback();
  $(window).off("scroll.plp-lazy-load");
  $('.pagination-alternative').show()
  if(lazyAnimation) {
    lazyAnimation.hide();
  }
}

/**
 * Initializes lazy loading for plp
 * @param  {jQuery} $el - The ul container element
 * @param  {Object} [options={}] - Any options provided upon initialization
 * @return {Object} Immutable version of the options*
 */
const lazyLoadPlp = ($el, options = {}) => {
  const $window = $(window)
  const lazyAnimation = $(progressBarHTML());  
  $.ajax({
    url: options.lazyLoadUrl,
    cache: false,
    success: function (result) {
      setSessionData(result, options.lazyLoadUrl, options.sessionName);
      plpAppendCleanup($el, options, result, lazyAnimation);
      setupYotpoObserver();
      checkAndHideZeroReviewElements();
    }
  }); 
  
  $window.on("scroll.plp-lazy-load", throttle(()=> {
    const windowHeight = $window.height();
    const scrollPosY = $window.scrollTop() + windowHeight;
    const targetTop = $el.offset().top;
    if(scrollPosY >= targetTop) {
      $el.after(lazyAnimation);
      $window.off("scroll.plp-lazy-load");
      $('.pagination-alternative').hide()
    }
  }, 10));
}
const progressBarHTML = () => {
  return `<div id="js-trigger-load-plp" class="u-padding-b--xl">
      <div class="progress-bar__lazy u-margin--center">
        <div class="u-padding-a--xl">
          <div class="progress-bar progress-bar--dark">
            <div class="u-screen-reader">
              Products are loading
            </div>
          </div>
        </div>
      </div>
    </div>`
}

const loadYotpoRatings = (productEl) => {
  if (window.yotpoWidgetsContainer && window.yotpoWidgetsContainer.initWidgets) {
    window.yotpoWidgetsContainer.initWidgets(false);
    if (productEl.dataset.yotpoElementUninitialized === 'true') {
      productEl.classList.add('yotpo-widget-instance');
      productEl.removeAttribute('data-yotpo-element-uninitialized');
    }
  } else {
    console.warn('Yotpo library not found or not ready. Check if script has loaded.');
  }
};

const setupYotpoObserver = () => {
  if ('IntersectionObserver' in window) {
    const options = {
      root: null,
      rootMargin: '0px 0px 1400px 0px',
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const productElement = entry.target;
          const { yotpoProductId: productId } = productElement.dataset;

          if (productId) {
            loadYotpoRatings(productElement);
          }
          // Prevent observation when user scrolls back up/down
          observer.unobserve(productElement);
        }
      });
    }, options);

    document.querySelectorAll('.yotpo-widget-instance:not([data-yotpo-element-loaded="true"])')
        .forEach(widget => {
          observer.observe(widget);
          widget.classList.remove('yotpo-widget-instance');
          widget.setAttribute('data-yotpo-element-uninitialized', 'true');
        });
  } else {
    // IntersectionObserver unsupported, use old code
    if (typeof yotpoWidgetsContainer !== 'undefined' && typeof yotpoWidgetsContainer.initWidgets !== 'undefined') {
      yotpoWidgetsContainer.initWidgets(false);
    }
  }
};

/**
 * Initializes plpLazyLoad.
 * @param {jQuery} el - targeting the element of the plp list
 * @param {Object} options - Options used for wiring up the plpLazyLoad
 * @see DEFAULTS
 */
export default function plpLazyLoad(el, options = {}) {
  const $el = $(el);
  const opts = mergeOptions(DEFAULTS, options, $el);
  const sessionArray = JSON.parse(sessionStorage.getItem(options.sessionName)) || [];
  // Checks if there's an object with current URL already
  const getObjFromPlpSessionData = sessionArray.filter(obj => {
    return obj.currentUrl === window.location.href && obj.lazyLoadUrl === options.lazyLoadUrl;
  })

  if (getObjFromPlpSessionData.length > 0) {
    plpAppendCleanup($el, opts, getObjFromPlpSessionData[0].plpData);
  } else {
    lazyLoadPlp($el, opts);
  }
}

registerJQueryPlugin(PLUGIN_NAME, plpLazyLoad);


// WEBPACK FOOTER //
// ./src/scripts/entry-points/plp/plp-lazy-load.js